import { isEntityCall } from '@almadar/core';
import { sampleRowCount, sampleFieldValue, sampleRow } from '@almadar/core/mock';
import { createLogger, isLogLevelEnabled } from '@almadar/logger';

// src/ui/contract-errors.ts
var RendererContractViolationError = class extends Error {
  constructor(message) {
    super(message);
    this.name = "RendererContractViolationError";
  }
};

// src/ui/slots.ts
function aggregateSlotContent(slot, sources) {
  const entries = Array.from(sources.values());
  if (entries.length === 0) return void 0;
  if (entries.length === 1) return entries[0];
  Array.from(sources.keys());
  const children = entries.map((entry) => ({
    type: entry.pattern?.type ?? "box",
    ...entry.props
  }));
  return {
    slot,
    pattern: { type: "stack", direction: "vertical", gap: "lg", children },
    props: { direction: "vertical", gap: "lg", children },
    source: entries[0].source
  };
}
function isNonNullObject(value) {
  return typeof value === "object" && value !== null;
}
function assertHasFunctionMethods(label, value, methods) {
  for (const method of methods) {
    if (typeof value[method] !== "function") {
      throw new RendererContractViolationError(`${label}.${method} must be a function`);
    }
  }
}
function assertIsSlotManager(value) {
  if (!isNonNullObject(value)) {
    throw new RendererContractViolationError("SlotManager must be a non-null object");
  }
  assertHasFunctionMethods("SlotManager", value, [
    "getContent",
    "setContent",
    "clearSlot",
    "getAllSlots",
    "subscribe"
  ]);
}
function assertIsMultiSourceSlotManager(value) {
  if (!isNonNullObject(value)) {
    throw new RendererContractViolationError("MultiSourceSlotManager must be a non-null object");
  }
  assertHasFunctionMethods("SlotManager", value, [
    "getContent",
    "setContent",
    "clearSlot",
    "getAllSlots",
    "subscribe"
  ]);
  assertHasFunctionMethods("MultiSourceSlotManager", value, [
    "getTraitContent",
    "subscribeTrait",
    "updateTraitContent"
  ]);
}
function validateSlotContent(content) {
  const errors = [];
  if (content.slot === void 0 || content.slot === null) {
    errors.push({ message: "SlotContent.slot is required", path: "slot" });
  }
  if (!("pattern" in content)) {
    errors.push({ message: "SlotContent.pattern is required", path: "pattern" });
  }
  return errors;
}
function createSlotSetter(manager) {
  return {
    addPattern: (slot, pattern, props) => {
      manager.setContent({
        slot,
        pattern,
        props
      });
    },
    clearSlot: (slot) => {
      manager.clearSlot(slot);
    }
  };
}

// src/ui/wrapCallbackForEvent.ts
function wrapCallbackForEvent(qualifiedEvent, callbackArgs, emit) {
  const argNames = (callbackArgs ?? []).map((a) => a.name);
  if (argNames.length === 0) {
    return () => emit(qualifiedEvent);
  }
  return (...args) => {
    const payload = {};
    for (let i = 0; i < argNames.length; i += 1) {
      payload[argNames[i]] = args[i];
    }
    emit(qualifiedEvent, payload);
  };
}
var PERF_NAMESPACE = "almadar:perf:canvas";
var log = createLogger(PERF_NAMESPACE);
var RING_SIZE = 50;
var ring = [];
var writeIdx = 0;
var subscribers = /* @__PURE__ */ new Set();
var notifyScheduled = false;
var revision = 0;
var cachedSnapshot = [];
var cachedRevision = -1;
function scheduleNotify() {
  if (notifyScheduled) return;
  notifyScheduled = true;
  queueMicrotask(() => {
    notifyScheduled = false;
    revision++;
    for (const fn of subscribers) fn();
  });
}
function push(entry) {
  if (ring.length < RING_SIZE) {
    ring.push(entry);
  } else {
    ring[writeIdx] = entry;
  }
  writeIdx = (writeIdx + 1) % RING_SIZE;
  scheduleNotify();
}
function isEnabled() {
  return isLogLevelEnabled("DEBUG", PERF_NAMESPACE) || globalThis.__ALMADAR_PERF__ === true;
}
function isVerbose() {
  return isLogLevelEnabled("DEBUG", PERF_NAMESPACE);
}
function now() {
  return typeof performance !== "undefined" && typeof performance.now === "function" ? performance.now() : Date.now();
}
function perfStart(name) {
  if (!isEnabled()) return -1;
  if (typeof performance !== "undefined" && typeof performance.mark === "function") {
    try {
      performance.mark(`${name}-start`);
    } catch {
    }
  }
  return now();
}
function perfEnd(name, startToken, detail) {
  if (startToken < 0 || !isEnabled()) return;
  const endTs = now();
  const durationMs = endTs - startToken;
  if (typeof performance !== "undefined" && typeof performance.measure === "function") {
    try {
      performance.mark(`${name}-end`);
      performance.measure(name, `${name}-start`, `${name}-end`);
    } catch {
    }
  }
  push({ name, durationMs, ts: endTs, detail });
  aggregate(name, durationMs);
  if (isVerbose()) log.debug(name, () => ({ durationMs, ...detail ?? {} }));
}
function perfTime(name, fn, detail) {
  const t = perfStart(name);
  try {
    return fn();
  } finally {
    perfEnd(name, t, detail);
  }
}
async function perfTimeAsync(name, fn) {
  const t = perfStart(name);
  try {
    return await fn();
  } finally {
    perfEnd(name, t);
  }
}
function perfGauge(name, value) {
  if (!isEnabled()) return;
  aggregate(name, value);
}
var buckets = /* @__PURE__ */ new Map();
var dumpTimer = null;
function aggregate(name, ms) {
  const b = buckets.get(name);
  if (b) {
    b.count++;
    b.totalMs += ms;
    if (ms > b.maxMs) b.maxMs = ms;
  } else {
    buckets.set(name, { count: 1, totalMs: ms, maxMs: ms });
  }
  if (dumpTimer === null) {
    dumpTimer = setInterval(dumpSummary, 5e3);
    if (typeof dumpTimer === "object" && "unref" in dumpTimer) dumpTimer.unref();
  }
}
function dumpSummary() {
  if (buckets.size === 0) return;
  const rows = [...buckets.entries()].map(([name, b]) => ({
    name,
    count: b.count,
    totalMs: Math.round(b.totalMs),
    avgMs: Math.round(b.totalMs / b.count * 100) / 100,
    maxMs: Math.round(b.maxMs * 10) / 10
  })).sort((a, b) => b.totalMs - a.totalMs);
  log.warn("[PROFILE] summary", { phases: JSON.stringify(rows) });
  buckets.clear();
}
function getPerfSnapshot() {
  if (ring.length < RING_SIZE) return ring.slice();
  return [...ring.slice(writeIdx), ...ring.slice(0, writeIdx)];
}
function getSnapshot() {
  if (cachedRevision !== revision) {
    cachedSnapshot = getPerfSnapshot();
    cachedRevision = revision;
  }
  return cachedSnapshot;
}
function subscribe(fn) {
  subscribers.add(fn);
  return () => {
    subscribers.delete(fn);
  };
}
function pushPerfEntry(entry) {
  if (!isEnabled()) return;
  push(entry);
}
function clearPerf() {
  ring.length = 0;
  writeIdx = 0;
  scheduleNotify();
}
var perfStore = {
  subscribe,
  getSnapshot
};

// src/ui/prepareSchemaForPreview.ts
function generateEntityRow(entity, idx) {
  return {
    ...sampleRow(
      { name: entity.name ?? "Entity", persistence: entity.persistence, fields: entity.fields },
      { index: idx, strategy: "index", persistence: entity.persistence }
    ),
    id: String(idx)
  };
}
function buildMockData(schema) {
  const t = perfStart("build-mock-data");
  const result = {};
  for (const orbital of schema.orbitals) {
    const entity = orbital.entity;
    if (!entity || typeof entity === "string") continue;
    if (isEntityCall(entity)) continue;
    const entityName = entity.name;
    if (!entityName) continue;
    if (entity.instances && entity.instances.length > 0) {
      result[entityName] = entity.instances;
      continue;
    }
    const count = sampleRowCount(
      { name: entityName, persistence: entity.persistence, fields: entity.fields },
      10
    );
    result[entityName] = Array.from({ length: count }, (_, i) => generateEntityRow(entity, i + 1));
  }
  for (const orbital of schema.orbitals) {
    for (const traitRef of orbital.traits ?? []) {
      if (!isInlineTrait(traitRef)) continue;
      const trait = traitRef;
      const sourceEntity = trait.sourceEntityDefinition;
      if (!sourceEntity || isEntityCall(sourceEntity)) continue;
      const sourceName = sourceEntity.name;
      if (!sourceName) continue;
      if (!result[sourceName]) {
        result[sourceName] = sourceEntity.instances && sourceEntity.instances.length > 0 ? sourceEntity.instances : Array.from(
          { length: 10 },
          (_, i) => generateEntityRow(sourceEntity, i + 1)
        );
      }
      const reboundName = trait.linkedEntity;
      if (!reboundName || reboundName === sourceName) continue;
      const reboundRows = result[reboundName];
      if (!reboundRows || reboundRows.length === 0) continue;
      reboundRows.forEach((row, i) => {
        for (const f of sourceEntity.fields) {
          if (f.name === void 0 || f.name === "id") continue;
          if (row[f.name] !== void 0) continue;
          const value = sampleFieldValue(f, {
            entityName: sourceName,
            index: i + 1,
            strategy: "index",
            persistence: sourceEntity.persistence
          });
          if (value !== void 0) row[f.name] = value;
        }
      });
    }
  }
  perfEnd("build-mock-data", t, { orbitalCount: schema.orbitals.length, entityCount: Object.keys(result).length });
  return result;
}
function isInlineTrait(traitRef) {
  return typeof traitRef === "object" && traitRef !== null && "stateMachine" in traitRef;
}
function findDataState(sm, initialStateName) {
  return sm.states.find((s) => {
    if (s.name === initialStateName) return false;
    return sm.transitions.some(
      (t) => t.event === "INIT" && (t.from === s.name || Array.isArray(t.from) && t.from.includes(s.name))
    );
  });
}
function rewriteTraitInitialState(trait, mockData) {
  const sm = trait.stateMachine;
  if (!sm) return trait;
  const linkedEntity = trait.linkedEntity;
  if (!linkedEntity || !mockData[linkedEntity]?.length) return trait;
  const initialStateName = sm.states.find((s) => s.isInitial)?.name ?? sm.states[0]?.name;
  if (!initialStateName) return trait;
  const dataState = findDataState(sm, initialStateName);
  if (!dataState) return trait;
  const updatedStates = sm.states.map((s) => {
    if (s.name === initialStateName) return { ...s, isInitial: false };
    if (s.name === dataState.name) return { ...s, isInitial: true };
    return s;
  });
  return { ...trait, stateMachine: { ...sm, states: updatedStates } };
}
function adjustSchemaForMockData(schema, mockData) {
  let changed = false;
  const updatedOrbitals = schema.orbitals.map((orbital) => {
    const traits = orbital.traits ?? [];
    const updatedTraits = traits.map((traitRef) => {
      if (!isInlineTrait(traitRef)) return traitRef;
      const updated = rewriteTraitInitialState(traitRef, mockData);
      if (updated !== traitRef) changed = true;
      return updated;
    });
    return changed ? { ...orbital, traits: updatedTraits } : orbital;
  });
  return changed ? { ...schema, orbitals: updatedOrbitals } : schema;
}
function prepareSchemaForPreview(input) {
  const parsed = typeof input === "string" ? JSON.parse(input) : input;
  const mockData = buildMockData(parsed);
  const schema = adjustSchemaForMockData(parsed, mockData);
  return { schema, mockData };
}

// src/ui/orbitalsByTrait.ts
function buildOrbitalsByTrait(schema, resolvedPages = []) {
  const map = {};
  if (!schema?.orbitals) return map;
  const pagePathToOrbital = {};
  for (const orb of schema.orbitals) {
    for (const traitRef of orb.traits ?? []) {
      let traitName;
      if (typeof traitRef === "string") {
        const parts = traitRef.split(".");
        traitName = parts[parts.length - 1];
      } else if ("ref" in traitRef && typeof traitRef.ref === "string") {
        const parts = traitRef.ref.split(".");
        traitName = traitRef.name ?? parts[parts.length - 1];
      } else if ("name" in traitRef && typeof traitRef.name === "string") {
        traitName = traitRef.name;
      }
      if (traitName) map[traitName] = orb.name;
    }
    for (const pg of orb.pages ?? []) {
      const path = typeof pg === "string" ? pg : pg?.path;
      if (path) pagePathToOrbital[path] = orb.name;
    }
  }
  for (const page of resolvedPages) {
    const orbital = page.path ? pagePathToOrbital[page.path] : void 0;
    if (!orbital) continue;
    for (const traitName of page.traitNames) {
      if (traitName && !(traitName in map)) map[traitName] = orbital;
    }
  }
  return map;
}
var log2 = createLogger("almadar:runtime:verify");
var MAX_EVENT_LOG = 200;
var EMPTY_SUMMARY = {
  totalChecks: 0,
  passed: 0,
  failed: 0,
  warnings: 0,
  pending: 0
};
function emptySnapshot() {
  return {
    checks: [],
    transitions: [],
    bridge: null,
    summary: EMPTY_SUMMARY,
    traits: []
  };
}
function ensureVerificationApi() {
  if (typeof window === "undefined") return void 0;
  if (!window.__orbitalVerification) {
    window.__orbitalVerification = {
      getSnapshot: emptySnapshot,
      getChecks: () => [],
      getTransitions: () => [],
      getBridge: () => null,
      getSummary: () => EMPTY_SUMMARY,
      waitForTransition: () => Promise.resolve(null),
      getTraitSnapshots: () => []
    };
  }
  return window.__orbitalVerification;
}
function getOrbitalVerification() {
  if (typeof window === "undefined") return void 0;
  return window.__orbitalVerification;
}
function bindEventBus(eventBus) {
  const api = ensureVerificationApi();
  if (!api) return;
  api.sendEvent = (event, payload, traitScope) => {
    const prefixed = event.startsWith("UI:") ? event : traitScope ? `UI:${traitScope}.${event}` : `UI:${event}`;
    log2.debug("sendEvent", {
      event: prefixed,
      traitScope,
      payloadKeys: payload ? Object.keys(payload) : []
    });
    eventBus.emit(prefixed, payload);
  };
  const eventLog = [];
  api.eventLog = eventLog;
  api.clearEventLog = () => {
    eventLog.length = 0;
  };
  if (eventBus.onAny) {
    const verificationEventLogger = (event) => {
      if (eventLog.length < MAX_EVENT_LOG) {
        eventLog.push({
          type: event.type,
          payload: event.payload,
          timestamp: Date.now()
        });
      }
    };
    Object.defineProperty(verificationEventLogger, "name", {
      value: "runtime-verify:eventLog"
    });
    eventBus.onAny(verificationEventLogger);
  }
}
function bindTraitStateGetter(getter) {
  const api = ensureVerificationApi();
  if (!api) return;
  api.getTraitState = getter;
}

export { PERF_NAMESPACE, RendererContractViolationError, adjustSchemaForMockData, aggregateSlotContent, assertIsMultiSourceSlotManager, assertIsSlotManager, bindEventBus, bindTraitStateGetter, buildMockData, buildOrbitalsByTrait, clearPerf, createSlotSetter, ensureVerificationApi, getOrbitalVerification, perfEnd, perfGauge, perfStart, perfStore, perfTime, perfTimeAsync, prepareSchemaForPreview, pushPerfEntry, validateSlotContent, wrapCallbackForEvent };
